import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { getProvider } from '../lib/voiceProviders';
import { getVoiceProvider } from '../services/settingsService';
import assistantService from '../services/assistantService';
import assistantConfigService from '../services/assistantConfigService';
import { useAuth } from './AuthContext';

const VoiceCallContext = createContext();

export const useVoiceCall = () => {
  const context = useContext(VoiceCallContext);
  if (!context) throw new Error('useVoiceCall must be used within a VoiceCallProvider');
  return context;
};
// Back-compat alias; existing components import useVapi.
export const useVapi = useVoiceCall;

const DEFAULT_VAPI_CONFIG = {
  transcriber: { provider: 'deepgram', model: 'nova-2', language: 'en' },
  model: { provider: 'openai', model: 'gpt-4o-mini', messages: [{ role: 'system', content: 'You are a helpful AI assistant.' }] },
  voice: { provider: 'vapi', voiceId: 'Lily' },
  name: 'Default Assistant',
  firstMessage: 'Hello! How can I help you today?',
};

export const VoiceCallProvider = ({ children }) => {
  const { user, userProfile } = useAuth();
  const [isCallActive, setIsCallActive] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isAssistantMuted, setIsAssistantMuted] = useState(false);
  const [callStatus, setCallStatus] = useState('idle');
  const [transcript, setTranscript] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [userAssistants, setUserAssistants] = useState([]);
  const [callError, setCallError] = useState(null);
  const [currentCall, setCurrentCall] = useState(null);
  const [lastEndReason, setLastEndReason] = useState(null);
  const [activeProvider, setActiveProvider] = useState('vapi');
  const lastEndAtRef = useRef(0);
  // Seeded at mount so the binding effect below always has a provider to attach to;
  // startCall() swaps it per call.
  const providerRef = useRef(getProvider('vapi'));
  const isCallActiveRef = useRef(false);
  const lastEndReasonRef = useRef(null);

  useEffect(() => { isCallActiveRef.current = isCallActive; }, [isCallActive]);
  useEffect(() => { lastEndReasonRef.current = lastEndReason; }, [lastEndReason]);

  // Only a deliberate hang-up on either side is graceful. Retell's bare 'call-ended'
  // (dropped connection, agent crash) is not — retellProvider maps a user-initiated
  // stop() to 'customer-ended-call' so it lands here.
  const isGraceful = useCallback((reason) => (
    reason === 'assistant-ended-call' || reason === 'customer-ended-call'
  ), []);

  const markGracefulEnd = useCallback((reason) => {
    if (!reason) return;
    setLastEndReason(reason);
    lastEndAtRef.current = Date.now();
    if (isGraceful(reason)) {
      window._vapiCallManuallyEnded = true;
      window.markCallManuallyEnded?.(true);
    }
  }, [isGraceful]);

  const resetCallState = useCallback(() => {
    setIsCallActive(false);
    setCallStatus('idle');
    setTranscript('');
    setChatMessages([]);
    setCurrentCall(null);
  }, []);

  const appendTranscript = useCallback(({ role, text, mode }) => {
    if (!text) return;
    const now = new Date();
    setChatMessages((prev) => {
      const last = prev[prev.length - 1];
      if (mode === 'replace' && last && last.speaker === role) {
        return [...prev.slice(0, -1), { ...last, text, timestamp: now }];
      }
      const combine = last && last.speaker === role && now.getTime() - new Date(last.timestamp).getTime() < 4000;
      if (combine && mode === 'append') {
        return [...prev.slice(0, -1), { ...last, text: `${last.text} ${text}`, timestamp: now }];
      }
      return [...prev, { id: Date.now() + Math.random(), speaker: role, text, timestamp: now }];
    });
    if (mode === 'append') setTranscript((prev) => `${prev} ${text}`);
  }, []);

  // Bind normalized events to whichever provider is active. Rebinds when the provider changes.
  useEffect(() => {
    const provider = providerRef.current;

    const onCallStart = () => { setIsCallActive(true); setCallStatus('active'); setCallError(null); };
    const onCallEnd = ({ reason } = {}) => { markGracefulEnd(reason); resetCallState(); };
    const onSpeechStart = () => setCallStatus('listening');
    const onSpeechEnd = () => setCallStatus('processing');
    const onSpeakingStart = () => setIsSpeaking(true);
    const onSpeakingEnd = () => setIsSpeaking(false);
    const onStatusEnded = ({ reason }) => markGracefulEnd(reason || 'unknown');
    const onToolResult = ({ name }) => {
      if (name === 'memory_tool') {
        // Vapi: nudge the model to react to the retrieved memories. Retell: no-op (send is a no-op there).
        provider.send({ type: 'add-message', message: { role: 'system', content: "You just received the user's memories. Make a smart comment about one of the recent memories so that the user feels welcome." } });
      }
    };
    const onError = (error) => {
      const msg = (error?.message || '').toLowerCase();
      const endedRecently = Date.now() - lastEndAtRef.current < 2000;
      const graceful = isGraceful(lastEndReasonRef.current) || msg.includes('assistant-ended-call') || msg.includes('customer-ended-call') || msg.includes('meeting has ended') || msg.includes('ended due to ejection');
      // Legacy parity: the pre-refactor Vapi handler closed over a stale `isCallActive`
      // (effect deps were [user]) that was always false mid-call, so every Vapi error was
      // suppressed. Keep that on the Vapi path; Retell gets real error handling.
      const treatAsInactive = providerRef.current?.name === 'vapi' ? true : !isCallActiveRef.current;
      if (graceful || endedRecently || treatAsInactive) {
        setIsCallActive(false); setCallStatus('idle'); setCallError(null);
        return;
      }
      setCallError(error); setIsCallActive(false); setCallStatus('error');
      // Never leave a live call running with the widget back in a non-hangup state.
      providerRef.current?.stop({ deliberate: false }).catch((e) => console.error('stop after error failed:', e));
    };

    const b = [
      ['call-start', onCallStart], ['call-end', onCallEnd], ['speech-start', onSpeechStart], ['speech-end', onSpeechEnd],
      ['speaking-start', onSpeakingStart], ['speaking-end', onSpeakingEnd], ['transcript', appendTranscript],
      ['tool-result', onToolResult], ['status-ended', onStatusEnded], ['error', onError],
    ];
    b.forEach(([e, h]) => provider.on(e, h));
    return () => b.forEach(([e, h]) => provider.off(e, h));
  }, [activeProvider, user, isGraceful, markGracefulEnd, resetCallState, appendTranscript]);

  const loadUserAssistants = async (userId) => {
    try { setUserAssistants(await assistantService.getUserAssistants(userId)); }
    catch (error) { console.error('❌ Error loading user assistants:', error); }
  };

  const userData = () => (userProfile && user?.id ? {
    first_name: userProfile.first_name, last_name: userProfile.last_name, full_name: userProfile.full_name,
    coach_name: userProfile.coach?.first_name || null, coach_full_name: userProfile.coach?.full_name || null,
  } : null);

  // ---- Vapi path: identical to the pre-refactor logic ----
  const resolveVapiConfig = async (assistantIdOrConfig, selectedAssistant) => {
    if (selectedAssistant && typeof selectedAssistant === 'object') {
      return selectedAssistant.tools !== undefined
        ? assistantConfigService.buildVapiCallConfig(selectedAssistant, userData())
        : assistantService.buildVapiCallConfig(selectedAssistant);
    }
    if (typeof assistantIdOrConfig === 'string') return assistantIdOrConfig;
    if (assistantIdOrConfig && typeof assistantIdOrConfig === 'object') return assistantIdOrConfig;
    if (user?.id) {
      try {
        const { data: assigned, error } = await assistantConfigService.getUserAssistant(user.id);
        // `return await` (not bare `return`) so a rejection from buildVapiCallConfig is caught
        // below and falls back to the default assistant, matching the pre-refactor behavior.
        if (!error && assigned) return await assistantConfigService.buildVapiCallConfig(assigned, userData());
        throw new Error('No assigned assistant found');
      } catch {
        return import.meta.env.VITE_VAPI_DEFAULT_ASSISTANT_ID || DEFAULT_VAPI_CONFIG;
      }
    }
    return DEFAULT_VAPI_CONFIG;
  };

  // ---- Retell path: ask the edge function for a token ----
  const resolveRetellConfig = async (selectedAssistant) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('You must be signed in to start a call.');
    // Only a coach-assistant row (which carries `tools`) is a vapi_assistants id the
    // edge function can resolve. Legacy rows send {} so the server uses the assignment.
    const body = selectedAssistant?.tools !== undefined ? { assistant_id: selectedAssistant.id } : {};
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/retell-web-call`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}`, apikey: import.meta.env.VITE_SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Could not start Retell call (${res.status})`);
    return {
      accessToken: data.access_token,
      callId: data.call_id,
      transport: data.transport ?? undefined,
      url: data.url ?? undefined,
      iceServers: data.ice_servers ?? undefined,
    };
  };

  const startCall = async (assistantIdOrConfig = null, selectedAssistant = null) => {
    try {
      setCallError(null);
      setCallStatus('connecting');

      // Anonymous/trial callers have no Supabase session, and retell-web-call requires
      // a JWT, so they stay on Vapi regardless of the switch.
      const providerName = user?.id ? await getVoiceProvider() : 'vapi';
      const provider = getProvider(providerName);
      providerRef.current = provider;
      setActiveProvider(providerName);
      if (!provider.isAvailable()) throw new Error(`${providerName === 'retell' ? 'Retell' : 'Vapi'} is not initialized. Please check configuration.`);

      const config = providerName === 'retell'
        ? await resolveRetellConfig(selectedAssistant)
        : await resolveVapiConfig(assistantIdOrConfig, selectedAssistant);

      console.log(`🚀 Starting ${providerName} call...`);
      const { callId, raw } = await provider.start(config);
      setCurrentCall({ id: callId, provider: providerName, raw });

      // Vapi: link call → user client-side (the retell-web-call function does this server-side).
      if (providerName === 'vapi') {
        if (callId && user?.id) {
          const { error } = await supabase.from('call_users').insert([{ call_id: callId, user_id: user.id }]);
          if (error) console.error('💀 Error inserting call user:', error);
        } else {
          console.warn('⚠️ Skipped call_users insert — missing id/user', { callId, userId: user?.id });
        }
      }
    } catch (error) {
      console.error('❌ Error starting call:', error);
      let errorMessage = 'Failed to start contact';
      if (error.message) errorMessage = error.message;
      else if (error.error?.message) errorMessage = error.error.message;
      else if (error.type === 'start-method-error') {
        if (error.error?.status === 400) errorMessage = 'Invalid call configuration. Please check your assistant settings.';
        else if (error.error?.type === 'cors') errorMessage = 'Network error. Please check your internet connection and try again.';
        else errorMessage = 'Failed to connect to voice service. Please try again.';
      }
      setCallError({ message: errorMessage, details: error });
      setCallStatus('error');
      setIsCallActive(false);
      throw new Error(errorMessage);
    }
  };

  const endCall = async () => {
    const provider = providerRef.current;
    try { if (provider) await provider.stop(); }
    catch (error) { console.error('❌ Error ending call:', error); }
    finally { resetCallState(); setCallError(null); }
  };

  const setMuted = (muted) => providerRef.current?.setMuted(muted);
  const setAssistantMuted = (muted) => setIsAssistantMuted(muted);

  // Assistant-side mute: mute every <audio> element (unchanged behavior).
  useEffect(() => {
    const apply = () => document.querySelectorAll('audio').forEach((a) => { a.muted = isAssistantMuted; });
    apply();
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => m.addedNodes.forEach((node) => {
        if (node.nodeName === 'AUDIO') node.muted = isAssistantMuted;
        else if (node.querySelectorAll) node.querySelectorAll('audio').forEach((a) => { a.muted = isAssistantMuted; });
      }));
      if (isAssistantMuted) apply();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isAssistantMuted]);

  const value = {
    isCallActive, isSpeaking, isAssistantMuted, callStatus, transcript, chatMessages, userAssistants, callError,
    currentCall, activeProvider,
    setMuted, setAssistantMuted, startCall, endCall, loadUserAssistants,
    vapiAvailable: getProvider('vapi').isAvailable(),
  };

  return <VoiceCallContext.Provider value={value}>{children}</VoiceCallContext.Provider>;
};
// Back-compat alias for App.jsx.
export const VapiProvider = VoiceCallProvider;
